import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaImage,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaSignInAlt,
  FaTrash,
  FaUndo,
  FaUser,
} from 'react-icons/fa';
import { useActivityLog } from '../../context/ActivityLogContext';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import {
  formatActivityType,
  formatLocation,
  formatTimestamp,
} from '../../services/activityLog';
import {
  canRevertEntry,
  formatActorLabel,
  formatChangePreview,
} from '../../utils/activityLogDisplay';
import './activityLog.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'login', label: 'Sign ins' },
  { id: 'edits', label: 'Text' },
  { id: 'media', label: 'Media' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'reset', label: 'Resets' },
];

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getDateGroupLabel(isoString) {
  const date = new Date(isoString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getFilterCategory(type) {
  if (type === 'login') return 'login';
  if (type === 'text') return 'edits';
  if (type === 'image' || type === 'gallery') return 'media';
  if (type === 'calendar' || type === 'service_times') return 'calendar';
  if (type === 'reset' || type === 'reset_all') return 'reset';
  return 'other';
}

function getEntryIcon(type) {
  if (type === 'login') return FaSignInAlt;
  if (type === 'image' || type === 'gallery') return FaImage;
  if (type === 'calendar' || type === 'service_times') return FaCalendarAlt;
  if (type === 'reset' || type === 'reset_all') return FaUndo;
  return FaEdit;
}

function formatPageLabel(page) {
  if (!page || page === '/') return 'Home';
  const name = page.replace(/^\//, '').replace(/-/g, ' ');
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function ChangeDetails({ entry }) {
  const hasSnapshot =
    entry.oldValue !== undefined ||
    entry.newValue !== undefined;

  if (!hasSnapshot) return null;

  return (
    <div className="activity-log-diff">
      <div className="activity-log-diff__col">
        <span className="activity-log-diff__label">Before</span>
        <p className="activity-log-diff__value">{formatChangePreview(entry.oldValue)}</p>
      </div>
      <div className="activity-log-diff__col">
        <span className="activity-log-diff__label">After</span>
        <p className="activity-log-diff__value">{formatChangePreview(entry.newValue)}</p>
      </div>
    </div>
  );
}

function ActivityEntryCard({ entry, isAdmin, onRevert, onRemove, isBusy }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = getEntryIcon(entry.type);
  const revertable = isAdmin && canRevertEntry(entry);

  return (
    <article className={`activity-log-card${entry.reverted ? ' activity-log-card--reverted' : ''}`}>
      <div className="activity-log-card__main">
        <div className={`activity-log-card__icon activity-log-card__icon--${entry.type}`}>
          <Icon aria-hidden="true" />
        </div>

        <div className="activity-log-card__content">
          <div className="activity-log-card__top">
            <span className={`activity-log-badge activity-log-badge--${entry.type}`}>
              {formatActivityType(entry.type, entry)}
            </span>
            {entry.reverted && <span className="activity-log-badge activity-log-badge--reverted">Reverted</span>}
            <time className="activity-log-card__time" dateTime={entry.timestamp}>
              {formatTimestamp(entry.timestamp)}
            </time>
          </div>

          <h3 className="activity-log-card__title">{entry.label}</h3>
          <p className="activity-log-card__summary">{entry.summary}</p>

          <div className="activity-log-card__meta">
            <span className="activity-log-meta-chip">
              <FaUser aria-hidden="true" />
              {formatActorLabel(entry)}
            </span>
            <span className="activity-log-meta-chip">Page: {formatPageLabel(entry.page)}</span>
            {entry.type === 'login' && entry.location && (
              <span className="activity-log-meta-chip">
                <FaMapMarkerAlt aria-hidden="true" />
                {formatLocation(entry.location)}
              </span>
            )}
          </div>

          {(entry.oldValue !== undefined || entry.newValue !== undefined) && (
            <button
              type="button"
              className="activity-log-expand"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
            >
              {expanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
              {expanded ? 'Hide details' : 'Show before / after'}
            </button>
          )}

          {expanded && <ChangeDetails entry={entry} />}
        </div>
      </div>

      {isAdmin && (
        <div className="activity-log-card__actions">
          {revertable && (
            <button
              type="button"
              className="activity-log-action activity-log-action--revert"
              disabled={isBusy}
              onClick={() => onRevert(entry)}
            >
              <FaUndo aria-hidden="true" />
              Revert change
            </button>
          )}
          <button
            type="button"
            className="activity-log-action activity-log-action--remove"
            disabled={isBusy}
            onClick={() => onRemove(entry)}
          >
            <FaTrash aria-hidden="true" />
            Remove
          </button>
        </div>
      )}
    </article>
  );
}

const ActivityLog = () => {
  const { logs, isLoading, refreshLogs, removeEntry, clearAll } = useActivityLog();
  const { isAdmin, userEmail } = useAuth();
  const { revertContentChange } = useContent();
  const [activeFilter, setActiveFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshLogs();
  }, [refreshLogs]);

  const stats = useMemo(() => ({
    total: logs.length,
    logins: logs.filter((entry) => entry.type === 'login').length,
    edits: logs.filter((entry) => getFilterCategory(entry.type) === 'edits').length,
    media: logs.filter((entry) => getFilterCategory(entry.type) === 'media').length,
    members: logs.filter((entry) => entry.loginRole === 'member' && entry.type !== 'login').length,
  }), [logs]);

  const filteredLogs = useMemo(() => {
    if (activeFilter === 'all') return logs;
    return logs.filter((entry) => getFilterCategory(entry.type) === activeFilter);
  }, [logs, activeFilter]);

  const groupedLogs = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    filteredLogs.forEach((entry) => {
      const label = getDateGroupLabel(entry.timestamp);
      if (!groupMap.has(label)) {
        const group = { label, entries: [] };
        groupMap.set(label, group);
        groups.push(group);
      }
      groupMap.get(label).entries.push(entry);
    });

    return groups;
  }, [filteredLogs]);

  const handleRevert = async (entry) => {
    if (!window.confirm(`Revert this change to "${entry.label}"?`)) return;

    setBusyId(entry.id);
    const result = await revertContentChange({
      contentKey: entry.contentKey,
      oldValue: entry.oldValue,
      entryId: entry.id,
      entryLabel: entry.label,
    });
    setBusyId(null);

    if (!result.ok) {
      window.alert(result.error || 'Could not revert this change.');
      return;
    }

    await refreshLogs();
  };

  const handleRemove = async (entry) => {
    if (!window.confirm('Remove this entry from the activity log?')) return;

    setBusyId(entry.id);
    const result = await removeEntry(entry.id);
    setBusyId(null);

    if (!result.ok) {
      window.alert(result.error || 'Could not remove this entry.');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear the entire activity log? This cannot be undone.')) return;
    setBusyId('clear-all');
    await clearAll();
    setBusyId(null);
  };

  return (
    <div className="activity-log-page">
      <div className="activity-log-shell">
        <header className="activity-log-hero">
          <div className="activity-log-hero__content">
            <p className="activity-log-eyebrow">Admin dashboard</p>
            <h1 className="activity-log-title">Activity Log</h1>
            <p className="activity-log-subtitle">
              Full history of sign-ins and content changes. Admins can revert edits or remove log entries.
            </p>
          </div>
          <Link to="/" className="activity-log-back">
            <FaArrowLeft aria-hidden="true" />
            Back to site
          </Link>
        </header>

        {isAdmin && (
          <div className="activity-log-admin-bar">
            <div className="activity-log-admin-bar__copy">
              <FaShieldAlt aria-hidden="true" />
              <span>
                Signed in as <strong>{userEmail}</strong> — you have full admin control over this log.
              </span>
            </div>
            <button
              type="button"
              className="activity-log-admin-bar__btn"
              disabled={busyId === 'clear-all' || logs.length === 0}
              onClick={handleClearAll}
            >
              Clear entire log
            </button>
          </div>
        )}

        <section className="activity-log-stats" aria-label="Activity summary">
          <div className="activity-log-stat">
            <span className="activity-log-stat__value">{stats.total}</span>
            <span className="activity-log-stat__label">Total events</span>
          </div>
          <div className="activity-log-stat">
            <span className="activity-log-stat__value">{stats.logins}</span>
            <span className="activity-log-stat__label">Sign ins</span>
          </div>
          <div className="activity-log-stat">
            <span className="activity-log-stat__value">{stats.edits}</span>
            <span className="activity-log-stat__label">Text edits</span>
          </div>
          <div className="activity-log-stat">
            <span className="activity-log-stat__value">{stats.media}</span>
            <span className="activity-log-stat__label">Media changes</span>
          </div>
        </section>

        <div className="activity-log-toolbar">
          <div className="activity-log-filters" role="tablist" aria-label="Filter activity">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={activeFilter === filter.id}
                className={`activity-log-filter ${activeFilter === filter.id ? 'is-active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <span className="activity-log-toolbar__count">
            {filteredLogs.length} shown
          </span>
        </div>

        {isLoading ? (
          <div className="activity-log-empty">
            <p className="activity-log-empty__title">Loading activity history…</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="activity-log-empty">
            <p className="activity-log-empty__title">
              {logs.length === 0 ? 'No activity yet' : 'No matches for this filter'}
            </p>
            <p className="activity-log-empty__text">
              {logs.length === 0
                ? 'When church members sign in and edit content, every change will appear here with who made it, when, and what changed.'
                : 'Try a different filter to see more history.'}
            </p>
          </div>
        ) : (
          <div className="activity-log-timeline">
            {groupedLogs.map((group) => (
              <section key={group.label} className="activity-log-day-group">
                <h2 className="activity-log-day-label">
                  <span>{group.label}</span>
                  <span className="activity-log-day-count">{group.entries.length}</span>
                </h2>
                <div className="activity-log-day-list">
                  {group.entries.map((entry) => (
                    <ActivityEntryCard
                      key={entry.id}
                      entry={entry}
                      isAdmin={isAdmin}
                      isBusy={busyId === entry.id}
                      onRevert={handleRevert}
                      onRemove={handleRemove}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
